import { useQuery } from "@apollo/client";
import { UseAutocompleteProps } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Typography, Box } from "@mui/material";
import { gql } from "generated-graphql";
import * as React from "react";

const GET_ALL_WORKFLOW_TAGS = gql(`
  query GetAllWorkflowTags {
    getAllWorkflowTags {
      id
      name
    }
  }
`);

type TagOption = {
  inputValue?: string;
  id?: string;
  name: string;
  isCommon?: boolean;
  isRecent?: boolean;
  isSelected?: boolean;
};

const filter = createFilterOptions<TagOption>();

type Props = {
  onAddTag: (tagName: string) => void;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  commonTags?: string[];
  recentTags?: string[];
  selectedTags?: string[];
};

export default function WorkflowTagPicker(props: Props) {
  const [value, setValue] = React.useState<TagOption | null>(null);
  const [inputValue, setInputValue] = React.useState("");
  const { data } = useQuery(GET_ALL_WORKFLOW_TAGS);
  const { commonTags = [], recentTags = [], selectedTags = [] } = props;

  const selectedTagsSet = React.useMemo(
    () => new Set(selectedTags),
    [selectedTags]
  );

  const tags: TagOption[] = React.useMemo(() => {
    // Build all available tags
    const allTags: TagOption[] = [];

    // Add common tags first
    commonTags.forEach((tagName) => {
      allTags.push({
        name: tagName,
        isCommon: true,
        isSelected: selectedTagsSet.has(tagName),
      });
    });

    // Add recent tags (but not duplicates of common tags)
    const commonTagsSet = new Set(commonTags);
    recentTags.forEach((tagName) => {
      if (!commonTagsSet.has(tagName)) {
        allTags.push({
          name: tagName,
          isRecent: true,
          isSelected: selectedTagsSet.has(tagName),
        });
      }
    });

    // Add all other tags from the database
    const usedTags = new Set([...commonTags, ...recentTags]);
    (data?.getAllWorkflowTags ?? []).forEach((tag) => {
      if (!usedTags.has(tag.name)) {
        allTags.push({
          id: tag.id,
          name: tag.name,
        });
      }
    });

    return allTags;
  }, [data?.getAllWorkflowTags, commonTags, recentTags, selectedTagsSet]);

  const onChange = React.useCallback(
    (
      event: React.SyntheticEvent<Element, Event>,
      newTag: string | TagOption | null
    ) => {
      // Ignore header selections
      if (
        newTag &&
        typeof newTag === "object" &&
        newTag.inputValue === "header"
      ) {
        return;
      }

      if (typeof newTag === "string") {
        props.onAddTag(newTag);
        setValue(null);
        setInputValue(""); // Clear the input text after adding
      } else if (newTag && newTag.inputValue) {
        // Create a new value from the user input
        props.onAddTag(newTag.inputValue);
        setValue(null);
        setInputValue(""); // Clear the input text after adding
      } else if (newTag) {
        props.onAddTag(newTag.name);
        setValue(null);
        setInputValue(""); // Clear the input text after adding
      }
    },
    [props.onAddTag]
  );

  const filterOptions: UseAutocompleteProps<
    TagOption,
    false,
    false,
    true
  >["filterOptions"] = (options, params) => {
    const filtered = filter(options, params);
    const { inputValue } = params;

    // Group filtered options
    const commonOptions = filtered.filter(
      (opt) => opt.isCommon && !opt.isSelected
    );
    const recentOptions = filtered.filter(
      (opt) => opt.isRecent && !opt.isSelected
    );
    const otherOptions = filtered.filter(
      (opt) => !opt.isCommon && !opt.isRecent
    );

    const result: TagOption[] = [];

    // Add commonly used section
    if (commonOptions.length > 0) {
      result.push({ name: "Commonly Used Tags", inputValue: "header" });
      result.push(...commonOptions);
    }

    // Add recent section
    if (recentOptions.length > 0) {
      result.push({ name: "Recent Tags", inputValue: "header" });
      result.push(...recentOptions);
    }

    // Add other options
    if (
      otherOptions.length > 0 &&
      (commonOptions.length > 0 || recentOptions.length > 0)
    ) {
      result.push({ name: "All Tags", inputValue: "header" });
    }
    result.push(...otherOptions);

    // Suggest the creation of a new value
    const isExisting = options.some((option) => inputValue === option.name);
    if (inputValue !== "" && !isExisting) {
      result.push({
        inputValue,
        name: `Add "${inputValue}"`,
      });
    }

    return result;
  };

  const getOptionLabel = React.useCallback((option: string | TagOption) => {
    // Value selected with enter, right from the input
    if (typeof option === "string") {
      return option;
    }
    // Add "xxx" option created dynamically
    if (option.inputValue) {
      return option.inputValue;
    }
    // Regular option
    return option.name;
  }, []);

  return (
    <Autocomplete
      freeSolo
      value={value}
      onChange={onChange}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        // Ignore "reset" events to prevent the selected tag from repopulating the input
        // after onChange clears it. This avoids duplicate tag additions on Enter.
        if (reason === "reset") {
          setInputValue("");
        } else {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={filterOptions}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      disabled={props.disabled}
      id="workflow-tag-picker"
      sx={{ width: "100%" }}
      options={tags}
      getOptionLabel={getOptionLabel}
      renderOption={(props, option) => {
        // Render section headers
        if (option.inputValue === "header") {
          return (
            <Box key={option.name} sx={{ px: 2, py: 1 }}>
              <Typography
                variant="caption"
                fontWeight="bold"
                color="text.secondary"
              >
                {option.name}
              </Typography>
            </Box>
          );
        }

        // Render regular options with indentation
        return (
          <li
            {...props}
            style={{
              ...props.style,
              opacity: option.isSelected ? 0.5 : 1,
              pointerEvents: option.isSelected ? "none" : "auto",
              paddingLeft: "32px", // Add indentation for tag items
            }}
          >
            {option.name}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Add workflow tag"
          inputRef={props.inputRef}
        />
      )}
    />
  );
}
